/**
 * Enhanced ProjectManager Discovery Component
 * AI-powered matching, comparison tools, and detailed portfolios for diaspora clients
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    Chip,
    Avatar,
    Rating,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Alert,
    AlertTitle,
    IconButton,
    Tooltip,
    Badge,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    CircularProgress
} from '@mui/material';
import {
    CompareArrows,
    CheckCircle,
    Star,
    Verified,
    TrendingUp,
    Work,
    Schedule,
    AttachMoney,
    PhotoLibrary,
    VideoLibrary,
    LocationOn,
    Phone,
    WhatsApp,
    Message,
    Close,
    EmojiEvents,
    Warning,
    Info,
    ThumbUp,
    Engineering,
    Send
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';

// Simple matching function for project managers
const matchProjectManagers = (projectManagers, requirements) => {
    return projectManagers.map(pm => {
        let matchScore = 50;
        const reasons = [];
        
        if (pm.location && requirements.location && 
            pm.location.toLowerCase().includes(requirements.location.toLowerCase())) {
            matchScore += 20;
            reasons.push(`Located in ${pm.location}`);
        }
        
        if (pm.rating >= 4.5) {
            matchScore += 15;
            reasons.push('Highly rated');
        }
        
        if (pm.experience && pm.experience.includes('years')) {
            const years = parseInt(pm.experience);
            if (years >= 5) {
                matchScore += 10;
                reasons.push(`${years}+ years experience`);
            }
        }
        
        if (pm.trustScore >= 80) {
            matchScore += 10;
            reasons.push('High trust score');
        }
        
        return {
            ...pm,
            matchScore: Math.min(100, matchScore),
            reasons: reasons.slice(0, 5)
        };
    }).sort((a, b) => b.matchScore - a.matchScore);
};

const EnhancedProjectManagerDiscovery = ({ projectManagers = [], projectType, location, budget }) => {
    const { user, getApiClient } = useAuth();
    const navigate = useNavigate();
    const [matchedProjectManagers, setMatchedProjectManagers] = useState([]);
    const [topRecommendation, setTopRecommendation] = useState(null);
    const [selectedForComparison, setSelectedForComparison] = useState([]);
    const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
    const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
    const [selectedProjectManager, setSelectedProjectManager] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
    const [quoteProjectManager, setQuoteProjectManager] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteSuccess, setQuoteSuccess] = useState(false);
    const [quoteError, setQuoteError] = useState('');
    const [quoteForm, setQuoteForm] = useState({
        projectDescription: '',
        projectLocation: location || '',
        estimatedBudget: budget || '',
        currency: 'USD',
        timeline: '',
        additionalNotes: ''
    });
    const [userProjects, setUserProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');

    useEffect(() => {
        loadRecommendations();
        loadUserProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectManagers, projectType, location, budget]);

    const loadUserProjects = async () => {
        try {
            const response = await api.get('/projects');
            const data = response.data;
            if (data.success && data.data && Array.isArray(data.data.projects)) {
                setUserProjects(data.data.projects);
            }
        } catch (error) {
            console.error('Failed to load projects:', error);
        }
    };

    const loadRecommendations = () => {
        try {
            setLoading(true);
            
            // Use intelligent matching algorithm
            const requirements = {
                projectType: projectType || 'residential',
                location: location || 'Harare',
                budget: budget || 50000,
                prioritize: 'balanced'
            };

            console.log('🤖 AI Matching projectManagers...', {
                projectManagers: projectManagers.length,
                requirements
            });

            // Match ALL projectManagers and get scores
            const allMatches = matchProjectManagers(projectManagers, requirements);
            
            console.log('✅ AI Matching complete:', allMatches.map(m => ({
                name: m.name,
                matchScore: m.matchScore,
                reasons: m.reasons.length
            })));

            setMatchedProjectManagers(allMatches);
            setTopRecommendation(allMatches.length > 0 ? allMatches[0] : null);
        } catch (error) {
            console.error('Error generating recommendations:', error);
            setMatchedProjectManagers(projectManagers);
            setTopRecommendation(null);
        } finally {
            setLoading(false);
        }
    };

    const toggleComparison = (projectManager) => {
        setSelectedForComparison(prev => {
            const exists = prev.find(c => c.id === projectManager.id);
            if (exists) {
                return prev.filter(c => c.id !== projectManager.id);
            }
            if (prev.length >= 3) {
                return [...prev.slice(1), projectManager];
            }
            return [...prev, projectManager];
        });
    };

    const [portfolioLoading, setPortfolioLoading] = useState(false);
    const [portfolioData, setPortfolioData] = useState(null);
    const [reviewsData, setReviewsData] = useState([]);

    const handleViewPortfolio = async (projectManager) => {
        setSelectedProjectManager(projectManager);
        setPortfolioDialogOpen(true);
        setActiveTab(0);
        setPortfolioLoading(true);
        
        try {
            // Fetch portfolio items (public endpoint)
            try {
                const portfolioResponse = await api.get(`/projectManagers/${projectManager.id}/portfolio`);
                const portfolioResult = portfolioResponse.data;
                if (portfolioResult.success) {
                    setPortfolioData(portfolioResult.data || []);
                } else {
                    setPortfolioData(projectManager.portfolio || []);
                }
            } catch {
                setPortfolioData(projectManager.portfolio || []);
            }

            // Fetch reviews (public endpoint)
            try {
                const reviewsResponse = await api.get(`/reviews/user/${projectManager.id}?limit=10`);
                const reviewsResult = reviewsResponse.data;
                if (reviewsResult.success) {
                    // Handle both array and object response formats
                    const reviews = Array.isArray(reviewsResult.data)
                        ? reviewsResult.data
                        : (reviewsResult.data?.reviews || []);
                    setReviewsData(reviews);
                } else {
                    setReviewsData(projectManager.recentReviews || []);
                }
            } catch {
                setReviewsData(projectManager.recentReviews || []);
            }
        } catch (error) {
            console.error('Failed to load portfolio data:', error);
            // Fallback to projectManager data if API fails
            setPortfolioData(projectManager.portfolio || []);
            setReviewsData(projectManager.recentReviews || []);
        } finally {
            setPortfolioLoading(false);
        }
    };

    const handleRequestQuote = (projectManager) => {
        setQuoteProjectManager(projectManager);
        setQuoteForm({
            projectDescription: '',
            projectLocation: location || '',
            estimatedBudget: budget || '',
            currency: 'USD',
            timeline: '',
            additionalNotes: ''
        });
        setSelectedProjectId('');
        setQuoteError('');
        setQuoteSuccess(false);
        setQuoteDialogOpen(true);
    };

    const handleSubmitQuoteRequest = async () => {
        if (!quoteProjectManager) return;

        setQuoteLoading(true);
        setQuoteError('');
        setQuoteSuccess(false);

        try {
            const quoteData = {
                projectManagerId: quoteProjectManager.id,
                projectManagerName: quoteProjectManager.name,
                projectId: selectedProjectId || null,
                projectDescription: quoteForm.projectDescription,
                projectLocation: quoteForm.projectLocation,
                estimatedBudget: quoteForm.estimatedBudget ? parseFloat(quoteForm.estimatedBudget) : null,
                currency: quoteForm.currency,
                timeline: quoteForm.timeline,
                additionalNotes: quoteForm.additionalNotes,
                clientId: user?.id,
                clientName: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.company_name || 'Client'
            };

            const response = await api.post('/quote-requests', quoteData);
            const data = response.data;

            if (data.success) {
                setQuoteSuccess(true);
                setTimeout(() => {
                    setQuoteDialogOpen(false);
                    setQuoteSuccess(false);
                }, 2000);
            } else {
                setQuoteError(data.message || 'Failed to send quote request');
            }
        } catch (error) {
            console.error('Quote request error:', error);
            setQuoteError('Failed to send quote request. Please try again.');
        } finally {
            setQuoteLoading(false);
        }
    };

    const getMatchScoreColor = (score) => {
        if (score >= 90) return 'success';
        if (score >= 75) return 'primary';
        if (score >= 60) return 'warning';
        return 'default';
    };

    const getTrustScoreColor = (score) => {
        if (score >= 85) return 'success.main';
        if (score >= 70) return 'warning.main';
        return 'error.main';
    };

    // AI Matching Panel
    const AIMatchingPanel = () => (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)', color: 'white' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                        <Engineering />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            AI-Powered ProjectManager Matching
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {loading ? 'Analyzing project managers...' : `We've analyzed ${projectManagers.length} project managers to find your perfect match`}
                        </Typography>
                    </Box>
                </Box>

                {loading && (
                    <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: 'text.primary' }}>
                        <Typography variant="body2">
                            🤖 AI is analyzing project managers based on your requirements...
                        </Typography>
                    </Alert>
                )}

                {!loading && topRecommendation && (
                    <Alert severity="success" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: 'text.primary' }}>
                        <AlertTitle>Top Recommendation</AlertTitle>
                        <strong>{topRecommendation.name}</strong> is the best match for your project management needs
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip label={`${topRecommendation.matchScore}% Match`} color="success" size="small" />
                            <Chip label={`Trust Score: ${topRecommendation.trustScore}%`} color="primary" size="small" />
                        </Box>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );

    // ProjectManager Match Card
    const ProjectManagerMatchCard = ({ projectManager, recommendation }) => (
        <Card sx={{ 
            height: '100%',
            position: 'relative',
            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: selectedForComparison.find(c => c.id === projectManager.id) ? '2px solid #1976d2' : 'none',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
            }
        }}>
            {/* Match Score Badge */}
            {recommendation && (
                <Chip 
                    label={`${recommendation.matchScore}% Match`}
                    color={getMatchScoreColor(recommendation.matchScore)}
                    size="small"
                    sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, fontWeight: 600 }}
                />
            )}

            <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                        {projectManager.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">{projectManager.name}</Typography>
                            {projectManager.verified && (
                                <Tooltip title="Verified by Eqaya">
                                    <Verified color="primary" fontSize="small" />
                                </Tooltip>
                            )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {projectManager.type} • {projectManager.experience}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Rating value={projectManager.rating} precision={0.1} readOnly size="small" />
                            <Typography variant="body2">
                                {projectManager.rating} ({projectManager.reviewCount} reviews)
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Trust Score */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            Trust Score
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color={getTrustScoreColor(projectManager.trustScore)}>
                            {projectManager.trustScore}%
                        </Typography>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={projectManager.trustScore} 
                        sx={{ 
                            height: 8, 
                            borderRadius: 1,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: getTrustScoreColor(projectManager.trustScore)
                            }
                        }}
                    />
                </Box>

                {/* Why This Match */}
                {recommendation && recommendation.reasons && (
                    <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'primary.50' }}>
                        <Typography variant="caption" fontWeight={600} color="primary.main">
                            Why this match?
                        </Typography>
                        <List dense disablePadding>
                            {recommendation.reasons.slice(0, 3).map((reason, idx) => (
                                <ListItem key={idx} disableGutters sx={{ py: 0.25 }}>
                                    <ListItemIcon sx={{ minWidth: 20 }}>
                                        <CheckCircle sx={{ fontSize: 14 }} color="success" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={reason}
                                        primaryTypographyProps={{ variant: 'caption' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}

                {/* Red Flags */}
                {projectManager.redFlags && projectManager.redFlags.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="caption">
                            <strong>Note:</strong> {projectManager.redFlags[0]}
                        </Typography>
                    </Alert>
                )}

                {/* Key Metrics */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="h6" color="primary">
                                {projectManager.completedProjects}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Projects
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={4}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="h6" color="success.main">
                                {projectManager.onTimeRate}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                On Time
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={4}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="h6" color="info.main">
                                {projectManager.responseRate}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Response
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Specialties */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                    {projectManager.specialties.slice(0, 4).map((specialty) => (
                        <Chip key={specialty} label={specialty} size="small" variant="outlined" />
                    ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PhotoLibrary />}
                        onClick={() => handleViewPortfolio(projectManager)}
                        fullWidth
                    >
                        View Profile
                    </Button>
                    <Button
                        variant={selectedForComparison.find(c => c.id === projectManager.id) ? 'contained' : 'outlined'}
                        size="small"
                        startIcon={<CompareArrows />}
                        onClick={() => toggleComparison(projectManager)}
                        fullWidth
                    >
                        {selectedForComparison.find(c => c.id === projectManager.id) ? 'Selected' : 'Compare'}
                    </Button>
                </Box>

                {/* Contact Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Message />}
                        fullWidth
                        color="primary"
                        onClick={() => navigate(`/messages?projectManagerId=${projectManager.id}`)}
                    >
                        Message
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => handleRequestQuote(projectManager)}
                    >
                        Contact
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );

    // Comparison Dialog
    const ComparisonDialog = () => (
        <Dialog
            open={comparisonDialogOpen}
            onClose={() => setComparisonDialogOpen(false)}
            maxWidth="lg"
            fullWidth
        >
            <DialogCloseButton onClick={() => setComparisonDialogOpen(false)} />
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Compare ProjectManagers</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {selectedForComparison.map((projectManager) => (
                        <Grid item xs={12} md={4} key={projectManager.id}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                                        <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 1, bgcolor: 'primary.main' }}>
                                            {projectManager.name.charAt(0)}
                                        </Avatar>
                                        <Typography variant="h6">{projectManager.name}</Typography>
                                        <Rating value={projectManager.rating} precision={0.1} readOnly size="small" />
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    {/* Comparison Metrics */}
                                    <List dense>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Completed Projects" 
                                                secondary={projectManager.completedProjects}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText 
                                                primary="On-Time Rate" 
                                                secondary={`${projectManager.onTimeRate}%`}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Response Rate" 
                                                secondary={`${projectManager.responseRate}%`}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Trust Score" 
                                                secondary={`${projectManager.trustScore}%`}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Experience" 
                                                secondary={projectManager.experience}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Location" 
                                                secondary={projectManager.location}
                                            />
                                        </ListItem>
                                    </List>

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{ mt: 2 }}
                                        onClick={() => {
                                            handleViewPortfolio(projectManager);
                                            setComparisonDialogOpen(false);
                                        }}
                                    >
                                        View Full Profile
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
        </Dialog>
    );

    // Portfolio Dialog
    const PortfolioDialog = () => {
        if (!selectedProjectManager) return null;

        return (
            <Dialog
                open={portfolioDialogOpen}
                onClose={() => setPortfolioDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogCloseButton onClick={() => setPortfolioDialogOpen(false)} />
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">{selectedProjectManager.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedProjectManager.type} • {selectedProjectManager.location}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 3 }}>
                        <Tab label="Portfolio" icon={<PhotoLibrary />} iconPosition="start" />
                        <Tab label="Reviews" icon={<Star />} iconPosition="start" />
                        <Tab label="Pricing" icon={<AttachMoney />} iconPosition="start" />
                    </Tabs>

                    {/* Portfolio Tab */}
                    {activeTab === 0 && (
                        <Box>
                            <Typography variant="body2" paragraph>
                                {selectedProjectManager.description || selectedProjectManager.bio || 'Professional projectManager with proven track record.'}
                            </Typography>

                            {portfolioLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Past Projects
                            </Typography>
                            
                                    {portfolioData && portfolioData.length > 0 ? (
                                        <Grid container spacing={2}>
                                            {portfolioData.map((item) => {
                                                // Portfolio items can have after_images array (JSON) or thumbnail_url
                                                let images = [];
                                                if (item.after_images) {
                                                    // Handle both JSON string and array
                                                    if (typeof item.after_images === 'string') {
                                                        try {
                                                            images = JSON.parse(item.after_images);
                                                        } catch (e) {
                                                            images = [item.after_images];
                                                        }
                                                    } else if (Array.isArray(item.after_images)) {
                                                        images = item.after_images;
                                                    }
                                                }
                                                const mainImage = images[0] || item.thumbnail_url || null;
                                                
                                                return (
                                                    <Grid item xs={12} sm={6} md={4} key={item.id || item.project_title}>
                                                        <Card sx={{ height: '100%' }}>
                                                            {mainImage && (
                                                                <Box
                                                                    component="img"
                                                                    src={mainImage}
                                                                    alt={item.project_title || 'Project'}
                                                                    sx={{
                                                                        width: '100%',
                                                                        height: 200,
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            )}
                                                            <CardContent>
                                                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                                                    {item.project_title || 'Project'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                                    {item.description || 'No description available'}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                                    {item.project_type && (
                                                                        <Chip label={item.project_type} size="small" />
                                                                    )}
                                                                    {item.project_category && (
                                                                        <Chip label={item.project_category} size="small" variant="outlined" />
                                                                    )}
                                                                </Box>
                                                                {item.project_value && (
                                                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                                                        {item.currency || 'USD'} {parseFloat(item.project_value).toLocaleString()}
                                                                    </Typography>
                                                                )}
                                                                {item.completion_date && (
                                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                                                        Completed: {new Date(item.completion_date).toLocaleDateString()}
                                                                    </Typography>
                                                                )}
                                                                {item.location && (
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                                                        {item.location}
                                                                    </Typography>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                            ) : (
                                <Alert severity="info">
                                            <AlertTitle>No Portfolio Items</AlertTitle>
                                            This projectManager hasn't added any portfolio items yet.
                                </Alert>
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    {/* Reviews Tab */}
                    {activeTab === 1 && (
                        <Box>
                            {portfolioLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : reviewsData && reviewsData.length > 0 ? (
                                reviewsData.map((review) => (
                                    <Paper key={review.id} sx={{ p: 2, mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Box>
                                                <Typography variant="subtitle2">
                                                    {review.reviewer_name || review.reviewer || 'Anonymous'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {review.created_at ? new Date(review.created_at).toLocaleDateString() : review.date || 'Recently'}
                                                </Typography>
                                            </Box>
                                            <Rating value={review.rating || 0} readOnly size="small" />
                                        </Box>
                                        <Typography variant="body2">{review.review_text || review.comment || 'No comment provided'}</Typography>
                                        {(review.is_verified || review.verified) && (
                                            <Chip 
                                                icon={<Verified fontSize="small" />} 
                                                label="Verified Review" 
                                                size="small" 
                                                color="success" 
                                                sx={{ mt: 1 }}
                                            />
                                        )}
                                    </Paper>
                                ))
                            ) : (
                                <Alert severity="info">
                                    <AlertTitle>No Reviews Yet</AlertTitle>
                                    This projectManager hasn't received any reviews yet. Be the first to leave a review after working with them.
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Pricing Tab */}
                    {activeTab === 2 && (
                        <Box>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <AlertTitle>Transparent Pricing</AlertTitle>
                                These are approximate prices. Final quotes may vary based on your specific project requirements.
                            </Alert>

                            {selectedProjectManager.priceTransparency && Object.keys(selectedProjectManager.priceTransparency).length > 0 ? (
                                <List>
                                    {Object.entries(selectedProjectManager.priceTransparency).map(([service, price]) => (
                                        <ListItem key={service}>
                                            <ListItemIcon>
                                                <AttachMoney color="primary" />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={service.replace(/([A-Z])/g, ' $1').trim()}
                                                secondary={price}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="info">
                                    Request a custom quote for detailed pricing
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPortfolioDialogOpen(false)}>Close</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<Message />} 
                        color="primary"
                        onClick={() => {
                            navigate(`/messages?projectManagerId=${selectedProjectManager.id}`);
                            setPortfolioDialogOpen(false);
                        }}
                    >
                        Send Message
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <Box>
            {/* AI Matching Panel */}
            <AIMatchingPanel />

            {/* Comparison Bar */}
            {selectedForComparison.length > 0 && (
                <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {selectedForComparison.length} project manager{selectedForComparison.length !== 1 ? 's' : ''} selected for comparison
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Select up to 3 project managers to compare side-by-side
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<CompareArrows />}
                                onClick={() => setComparisonDialogOpen(true)}
                                disabled={selectedForComparison.length < 2}
                            >
                                Compare Now
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Project Manager Cards */}
            <Grid container spacing={3}>
                {matchedProjectManagers.map((projectManager) => {
                    return (
                        <Grid item xs={12} md={6} lg={4} key={projectManager.id}>
                            <ProjectManagerMatchCard 
                                projectManager={projectManager} 
                                recommendation={projectManager.matchScore ? projectManager : null} 
                            />
                        </Grid>
                    );
                })}
            </Grid>

            {/* Dialogs */}
            <ComparisonDialog />
            <PortfolioDialog />
            
            {/* Quote Request Dialog */}
            <Dialog
                open={quoteDialogOpen}
                onClose={() => !quoteLoading && setQuoteDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogCloseButton onClick={() => setQuoteDialogOpen(false)} disabled={quoteLoading} />
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">Request Quote</Typography>
                            {quoteProjectManager && (
                                <Typography variant="body2" color="text.secondary">
                                    From {quoteProjectManager.name}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {quoteSuccess ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <AlertTitle>Quote Request Sent!</AlertTitle>
                            Your quote request has been sent to {quoteProjectManager?.name}. They will respond soon.
                        </Alert>
                    ) : (
                        <Box sx={{ pt: 2 }}>
                            {quoteError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {quoteError}
                                </Alert>
                            )}
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Link to Existing Project (Optional)</InputLabel>
                                        <Select
                                            value={selectedProjectId}
                                            onChange={(e) => setSelectedProjectId(e.target.value)}
                                            label="Link to Existing Project (Optional)"
                                        >
                                            <MenuItem value="">None - New Project</MenuItem>
                                            {userProjects.map((project) => (
                                                <MenuItem key={project.id} value={project.id}>
                                                    {project.name || project.title} - {project.location || 'Location TBD'}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Project Description"
                                        multiline
                                        rows={4}
                                        value={quoteForm.projectDescription}
                                        onChange={(e) => setQuoteForm({...quoteForm, projectDescription: e.target.value})}
                                        placeholder="Describe your project requirements..."
                                        required
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Project Location"
                                        value={quoteForm.projectLocation}
                                        onChange={(e) => setQuoteForm({...quoteForm, projectLocation: e.target.value})}
                                        placeholder="e.g., Borrowdale, Harare"
                                        required
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Estimated Budget"
                                        type="number"
                                        value={quoteForm.estimatedBudget}
                                        onChange={(e) => setQuoteForm({...quoteForm, estimatedBudget: e.target.value})}
                                        placeholder="0"
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Currency</InputLabel>
                                        <Select
                                            value={quoteForm.currency}
                                            onChange={(e) => setQuoteForm({...quoteForm, currency: e.target.value})}
                                            label="Currency"
                                        >
                                            <MenuItem value="USD">USD</MenuItem>
                                            <MenuItem value="GBP">GBP</MenuItem>
                                            <MenuItem value="EUR">EUR</MenuItem>
                                            <MenuItem value="ZAR">ZAR</MenuItem>
                                            <MenuItem value="ZWL">ZWL</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Timeline (Optional)"
                                        value={quoteForm.timeline}
                                        onChange={(e) => setQuoteForm({...quoteForm, timeline: e.target.value})}
                                        placeholder="e.g., 3-6 months, ASAP, etc."
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Additional Notes (Optional)"
                                        multiline
                                        rows={3}
                                        value={quoteForm.additionalNotes}
                                        onChange={(e) => setQuoteForm({...quoteForm, additionalNotes: e.target.value})}
                                        placeholder="Any specific requirements or questions..."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setQuoteDialogOpen(false)} 
                        disabled={quoteLoading}
                    >
                        {quoteSuccess ? 'Close' : 'Cancel'}
                    </Button>
                    {!quoteSuccess && (
                        <Button 
                            variant="contained" 
                            onClick={handleSubmitQuoteRequest}
                            disabled={quoteLoading || !quoteForm.projectDescription || !quoteForm.projectLocation}
                            startIcon={quoteLoading ? <CircularProgress size={16} /> : <Send />}
                        >
                            {quoteLoading ? 'Sending...' : 'Send Request'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EnhancedProjectManagerDiscovery;

